/**
 * Переход к вопросу FAQ по ссылке: куда поставить карточку, как крутить и как
 * перезапустить подсветку.
 *
 * Копия-вербатим в трёх читателях — витрина (frontend-web), справка персонала
 * (admin), справка водителя (driver-app); одинаковость сторожит
 * scripts/check-shared-copies.mjs. Переход по ссылке обязан выглядеть одинаково
 * везде, где FAQ читают. Сама подсветка — класс `.faq-flash` в globals.css
 * каждого приложения.
 */

/**
 * Карточка вопроса помечена атрибутом, а НЕ `id="q-<id>"`, хотя ссылка на неё —
 * `#q-<id>`. Будь у карточки такой id, браузер сам прыгал бы к ней при клике,
 * при загрузке страницы с этим адресом и при смене # — вопрос вставал к
 * верхнему краю, а после раскрытия ответа прокрутка revealQuestion вела его к
 * середине: страница скакала дважды. Без элемента с таким id прокрутка одна —
 * наша. Значение атрибута — id в нижнем регистре.
 */
export const QUESTION_CARD_ATTR = 'data-faq-question'

export function questionCard(id: string): Element | null {
  return document.querySelector(`[${QUESTION_CARD_ATTR}="${id.toLowerCase()}"]`)
}

/**
 * Адрес страницы следует за вопросом, который открыли рукой: открыл — адрес
 * `#q-<id>` (им можно поделиться), свернул тот, что в адресе, — `#` убирается.
 * Свернул другой — адрес не трогаем.
 *
 * replaceState, а не pushState: каждое раскрытие в истории превратило бы
 * «Назад» в перебор вопросов (в историю пишет только переход по ссылке). И
 * replaceState не шлёт hashchange — вопрос не переоткрывается и не
 * подсвечивается. history.state сохраняем: в нём своё хранит роутер Next.
 */
export function syncQuestionHash(id: string, open: boolean): void {
  const own = `#q-${id.toLowerCase()}`
  const current = window.location.hash.toLowerCase()
  const next = open ? own : current === own ? '' : current
  if (next === current) return
  const { pathname, search } = window.location
  window.history.replaceState(window.history.state, '', `${pathname}${search}${next}`)
}

/** Как выровнять карточку вопроса по вертикали. */
export type RevealBlock = 'center' | 'start'

/**
 * Карточка (вопрос с раскрытым ответом) помещается в окно — ставим её на
 * середину. Выше окна — к началу: центр отрезал бы сам вопрос, и читать
 * пришлось бы с середины ответа. `topInset` — отступ под липкую шапку
 * (scroll-margin-top карточки): он съедает окно сверху.
 */
export function revealBlock(cardHeight: number, viewportHeight: number, topInset = 0): RevealBlock {
  return cardHeight + topInset <= viewportHeight ? 'center' : 'start'
}

/** Плавно — кроме тех, кто попросил систему убрать движение. */
export function revealBehavior(reducedMotion: boolean): ScrollBehavior {
  return reducedMotion ? 'auto' : 'smooth'
}

/**
 * Подсветка открытого по ссылке вопроса. `n` растёт с каждым переходом и
 * служит ключом элемента подсветки: повторный переход на тот же вопрос
 * пересоздаёт элемент, и анимация идёт заново — без таймеров.
 */
export interface QuestionFlash {
  id: string
  n: number
}

export function nextFlash(prev: QuestionFlash | null, id: string): QuestionFlash {
  return { id, n: (prev?.n ?? 0) + 1 }
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

/** Высота области, где карточка прокручивается: ближайший прокручиваемый предок или окно. */
function scrollportHeight(card: Element): number {
  for (let node = card.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node)
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return Math.min(node.clientHeight, window.innerHeight)
    }
  }
  return window.innerHeight
}

/** Сколько кадров подряд карточка должна стоять на месте, чтобы прокрутка считалась законченной. */
export const SETTLE_FRAMES = 3
/** Дольше плавная прокрутка не идёт; если карточка всё ещё движется — подсвечиваем всё равно. */
export const SETTLE_MAX_MS = 1500

/**
 * Позвать `done`, когда карточка перестанет двигаться: прокрутка закончилась
 * (или её не было). Событие scrollend есть не во всех браузерах и не приходит,
 * если крутить было некуда, поэтому смотрим на саму карточку по кадрам.
 */
export function whenSettled(card: Element, done: () => void): void {
  const started = performance.now()
  let last = card.getBoundingClientRect().top
  let still = 0
  const tick = () => {
    const top = card.getBoundingClientRect().top
    still = Math.abs(top - last) < 0.5 ? still + 1 : 0
    last = top
    if (still >= SETTLE_FRAMES || performance.now() - started > SETTLE_MAX_MS) done()
    else requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
}

/**
 * Прокрутить к карточке вопроса. Звать после того, как ответ раскрылся:
 * решает полная высота карточки, а не высота одной строки вопроса.
 * `onSettled` — когда карточка встала на место: отсюда запускают подсветку,
 * иначе она отыграла бы, пока вопрос ещё за краем экрана.
 */
export function revealQuestion(card: Element | null, onSettled?: () => void): void {
  if (!card) return
  const style = (card as HTMLElement).style
  const inset = parseFloat(getComputedStyle(card).scrollMarginTop) || 0
  const block = revealBlock(card.getBoundingClientRect().height, scrollportHeight(card), inset)
  // Отступ под шапку нужен только при выравнивании к началу. При центрировании
  // браузер центрирует карточку ВМЕСТЕ с отступом, и она встаёт ниже середины
  // на половину отступа. Цель прокрутки считается в момент вызова, так что
  // отступ можно снять на сам вызов и тут же вернуть.
  const margin = style.scrollMarginTop
  if (block === 'center') style.scrollMarginTop = '0px'
  card.scrollIntoView({ block, behavior: revealBehavior(prefersReducedMotion()) })
  style.scrollMarginTop = margin
  if (onSettled) whenSettled(card, onSettled)
}
